import { getItemFromSessionStorage, setItemInSessionStorage } from "../hooks/sessionStorage";

// Recursively URL-decodes a string. Returns as soon as decodeURIComponent yields
// no further change, throws, or maxDepth is reached. Covers cases like
// https://zenvoya.ai/?https%3A%2F%2Fzenvoya.ai%3Fgclid%3Dtest123 where the real
// query string is buried inside a percent-encoded value.
export const recursivelyDecodeUrl = (url: string, maxDepth = 5): string => {
  let current = url;
  for (let i = 0; i < maxDepth; i++) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return current;
    }
    if (next === current) return current;
    current = next;
  }
  return current;
};

// Regex-extracts a tracking param from a URL after recursive decoding. Use this
// instead of URLSearchParams when the URL may be double/triple-encoded so the
// param name isn't visible to a standard query parser.
export const extractParamFromUrl = (url: string, paramName: string): string => {
  if (!url || !paramName) return "";
  const decoded = recursivelyDecodeUrl(url);
  const re = new RegExp(`[?&#]${paramName}=([^&#\\s]+)`, "i");
  const match = decoded.match(re);
  return match ? match[1] : "";
};

export interface ClickIds {
  gclid: string;
  gbraid: string;
  fbclid: string;
}

export const getClickIdsFromUrl = (url: string): ClickIds => ({
  gclid: extractParamFromUrl(url, "gclid"),
  gbraid: extractParamFromUrl(url, "gbraid"),
  fbclid: extractParamFromUrl(url, "fbclid"),
});

// Reads gclid/gbraid/fbclid from the current URL and persists them in
// sessionStorage if a value is present and not already stored. fbclid is stored
// in the Meta `_fbc` format (`fb.1.<ts>.<fbclid>`) under key `fbcId` to match
// the existing consumer in GAEvents.ts / usePixelEvent.ts.
export const persistClickIdsFromCurrentUrl = (): void => {
  if (typeof window === "undefined") return;
  const { gclid, gbraid, fbclid } = getClickIdsFromUrl(window.location.href);

  if (gclid && !getItemFromSessionStorage("gclid")) {
    setItemInSessionStorage("gclid", gclid);
  }
  if (gbraid && !getItemFromSessionStorage("gbraid")) {
    setItemInSessionStorage("gbraid", gbraid);
  }
  if (fbclid && !getItemFromSessionStorage("fbcId")) {
    setItemInSessionStorage("fbcId", `fb.1.${Date.now()}.${fbclid}`);
  }
};
